import { useState } from 'react'
import { HelpCircle, BookOpen, Keyboard, Database, BarChart3, Brain, FileText, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { supportService } from '@/services/support'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'

interface KeyboardShortcut {
  keys: string[]
  description: string
}

interface DocumentationLink {
  title: string
  description: string
  icon: React.ReactNode
  path: string
}

const keyboardShortcuts: KeyboardShortcut[] = [
  { keys: ['Cmd', 'K'], description: 'Open search' },
  { keys: ['?'], description: 'Show keyboard shortcuts' },
  { keys: ['Esc'], description: 'Close dialogs/panels' },
  { keys: ['/'], description: 'Focus search' },
]

const quickStartSteps: string[] = [
  'Connect a data source from the Data Sources page to import your customer or entity data',
  'Create a new model by selecting a use case (e.g., Churn Prediction, Capacity Forecasting)',
  'Train your model using the Quick Train feature or configure custom training parameters',
  'Make predictions using the trained model with real-time or batch prediction endpoints',
  'Monitor model performance and predictions from the Analytics dashboard',
]

const documentationLinks: DocumentationLink[] = [
  {
    title: 'Models',
    description: 'Learn how to create, train, and deploy ML models',
    icon: <Brain className="h-5 w-5" />,
    path: '/models',
  },
  {
    title: 'Predictions',
    description: 'Make real-time and batch predictions with your models',
    icon: <BarChart3 className="h-5 w-5" />,
    path: '/predictions',
  },
  {
    title: 'Data Sources',
    description: 'Connect and sync data from various sources',
    icon: <Database className="h-5 w-5" />,
    path: '/data-sources',
  },
  {
    title: 'Analytics',
    description: 'View insights and performance metrics',
    icon: <FileText className="h-5 w-5" />,
    path: '/analytics',
  },
]

export function HelpButton() {
  const [open, setOpen] = useState(false)
  const [supportSubject, setSupportSubject] = useState('')
  const [supportMessage, setSupportMessage] = useState('')
  const [supportSubmitting, setSupportSubmitting] = useState(false)
  const { toast } = useToast()

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (supportSubmitting) return
    setSupportSubmitting(true)
    const result = await supportService.createTicket({
      subject: supportSubject.trim(),
      message: supportMessage.trim(),
    })
    setSupportSubmitting(false)
    if (result.success) {
      setSupportSubject('')
      setSupportMessage('')
      toast({
        title: 'Message sent',
        description: "Your request is with our support team. We'll reply by email.",
      })
    } else {
      toast({
        variant: 'destructive',
        title: 'Could not send message',
        description:
          result.error.message ||
          'Something went wrong. Email support@brazilnut.ai instead.',
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Help and documentation">
          <HelpCircle className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Help & Documentation
          </SheetTitle>
          <SheetDescription>
            Get started with Brazilian Nuts ML platform
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Quick Start Guide */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Quick Start Guide
              </CardTitle>
              <CardDescription>
                Follow these steps to get started with the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {quickStartSteps.map((step, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                      {index + 1}
                    </span>
                    <span className="text-sm text-muted-foreground pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>

          {/* Keyboard Shortcuts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Keyboard className="h-4 w-4" />
                Keyboard Shortcuts
              </CardTitle>
              <CardDescription>
                Speed up your workflow with these shortcuts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {keyboardShortcuts.map((shortcut, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {shortcut.description}
                    </span>
                    <div className="flex gap-1">
                      {shortcut.keys.map((key, keyIndex) => (
                        <Badge key={keyIndex} variant="secondary" className="font-mono text-xs">
                          {key}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Documentation Links */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Documentation
              </CardTitle>
              <CardDescription>
                Explore detailed guides for each section
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {documentationLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.path}
                    className="flex items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-accent"
                    onClick={() => setOpen(false)}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      {link.icon}
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium leading-none">{link.title}</p>
                      <p className="text-sm text-muted-foreground">{link.description}</p>
                    </div>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Support Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Need Help?
              </CardTitle>
              <CardDescription>
                Send us a message and we'll get back to you by email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <form onSubmit={handleSupportSubmit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="support-subject">Subject</Label>
                  <Input
                    id="support-subject"
                    value={supportSubject}
                    onChange={(e) => setSupportSubject(e.target.value)}
                    placeholder="What do you need help with?"
                    maxLength={200}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="support-message">Message</Label>
                  <Textarea
                    id="support-message"
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    placeholder="Describe the issue or question, including what you were doing when it came up."
                    rows={4}
                    maxLength={10000}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={supportSubmitting || !supportSubject.trim() || !supportMessage.trim()}
                >
                  {supportSubmitting ? 'Sending…' : 'Send to Support'}
                </Button>
              </form>
              <div className="rounded-lg bg-muted p-3">
                <p className="text-xs text-muted-foreground">
                  We typically respond within 24 hours. You can also email{' '}
                  <a
                    href="mailto:support@brazilnut.ai"
                    className="text-primary hover:underline"
                  >
                    support@brazilnut.ai
                  </a>{' '}
                  directly.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Version Info */}
          <div className="text-center text-xs text-muted-foreground">
            Brazilian Nuts ML Platform v1.0.0
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
